import React from 'react';
import { Zap, CalendarClock, FileText, Handshake } from 'lucide-react';

/**
 * PayoutTermsSelector — the required per-gig Payout Terms picker used in the
 * admin AND client gig-creation forms (2026-06, V427).
 *
 * value: { payout_type, payout_terms_business_days, payout_custom_note }
 * onChange(nextValue)
 * allowCustom: expose the admin-only "Custom Payout Terms" option
 */
const STANDARD_DAYS = [15, 30, 45, 60, 90];

const OPTIONS = [
  { key: 'instant', label: 'Instant Pay', icon: Zap,
    desc: 'Qualifying gig processed through iEvntür. Eligible earnings are available for instant payout after approval.' },
  { key: 'standard', label: 'Standard Pay', icon: CalendarClock,
    desc: 'Payout issued on a net-day schedule after event completion and timesheet approval.' },
  { key: 'custom', label: 'Custom Payout Terms', icon: FileText, adminOnly: true,
    desc: 'Enter client-specific terms for this gig.' },
  { key: 'direct', label: 'Direct Booking / Agency Paid', icon: Handshake,
    desc: 'Paid directly by the booking party. Not processed through iEvntür.' },
];

export const PayoutTermsSelector = ({ value, onChange, allowCustom = false, required = true }) => {
  const v = value || {};
  const type = v.payout_type || '';
  const set = (patch) => onChange({ ...v, ...patch });

  const pick = (key) => {
    if (key === 'standard') set({ payout_type: 'standard', payout_expedited: false });
    else if (key === 'instant') set({ payout_type: 'instant', payout_expedited: true });
    else if (key === 'custom') set({ payout_type: 'custom', payout_expedited: false });
    else set({ payout_type: 'direct', payout_expedited: false });
  };

  return (
    <div data-testid="payout-terms-selector">
      <label className="block text-sm font-semibold text-gray-900 mb-1">
        Payout Terms {required && <span className="text-red-500">*</span>}
      </label>
      <p className="text-xs text-gray-500 mb-3">
        This is shown to talent before they apply or accept, and is locked in when they are booked.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {OPTIONS.filter((o) => !o.adminOnly || allowCustom).map((o) => {
          const active = type === o.key;
          const Icon = o.icon;
          return (
            <button
              type="button"
              key={o.key}
              onClick={() => pick(o.key)}
              className={`text-left rounded-lg border p-3 transition-colors ${
                active ? 'border-cyan-500 bg-cyan-50 ring-1 ring-cyan-400' : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
              data-testid={`payout-option-${o.key}`}
            >
              <div className="flex items-center gap-2">
                <Icon className={`w-4 h-4 ${active ? 'text-cyan-600' : 'text-gray-400'}`} />
                <span className="font-medium text-sm text-gray-900">{o.label}</span>
              </div>
              <p className="text-[11px] text-gray-500 mt-1 leading-snug">{o.desc}</p>
            </button>
          );
        })}
      </div>

      {type === 'standard' && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Payout timeframe</label>
          <div className="flex flex-wrap gap-2" data-testid="payout-standard-days">
            {STANDARD_DAYS.map((d) => (
              <button
                type="button"
                key={d}
                onClick={() => set({ payout_terms_business_days: d })}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  (v.payout_terms_business_days || 30) === d
                    ? 'border-cyan-500 bg-cyan-500 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                }`}
                data-testid={`payout-days-${d}`}
              >
                {d} days
              </button>
            ))}
          </div>
        </div>
      )}

      {type === 'custom' && allowCustom && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Custom terms shown to talent</label>
          <textarea
            className="w-full min-h-[70px] rounded-md border border-gray-300 p-2 text-sm"
            placeholder="e.g. Net 45 per client MSA; paid on the 1st and 15th…"
            value={v.payout_custom_note || ''}
            onChange={(e) => set({ payout_custom_note: e.target.value })}
            data-testid="payout-custom-note"
          />
        </div>
      )}
    </div>
  );
};

export default PayoutTermsSelector;
